import { TCoordinate } from "@api/api.int";
import { IGuard, TGuardKind } from "@api/auth.int";
import { IProfileGuardPostApi } from "@api/profile.int";
import { Place } from "@aws-sdk/client-location";
import { Box, BoxProps, Button, FormControl, FormErrorMessage, FormHelperText, FormLabel, IconButton, Image, Input, ListItem, Text, Textarea, UnorderedList, useBoolean } from "@chakra-ui/react";
import { useDeepAuth, useVVApi } from "components/api";
import { GUARD_KINDS, GUARD_KINDS_ARR } from "components/appComponents";
import { DeepErrorContainer } from "components/misc";
import { useVVStore } from "components/store";
import { AFFAIR_DESCRIPTION_MAX, AFFAIR_DESCRIPTION_MIN, GUARD_NAME_MAX, GUARD_NAME_MIN } from "components/utils";
import { PeticioLink, VizKoalicioLink, VizvButtonBack, VizvButtonCancel, VizvCard, VizvCardAction, VizvCardActionStrip, VizvCardContent, VizvCardHeader, VizvCardHeaderPopup, VizvMainCol } from "components/vizvComponents";
import { AffairLocation } from "features/affair/AffairLocation";
import { AuthWarningBox } from "features/auth/AuthWarningBox";
import { DeepAuthContext } from "features/auth/DeepAuth";
import { HUNGARY_CENTER } from "features/map/MyMap";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { MdEdit } from "react-icons/md";
import { NavLink, Outlet, useNavigate } from "react-router";


// ---------------------------------------------- GUARD RANK ------------------------------------

export interface GuardKindBoxProps extends BoxProps {
	guardKind: TGuardKind;
	guardName?: string;
	selected?: boolean;
}

export function GuardKindBox({ guardKind, guardName, selected = false, ...rest }: GuardKindBoxProps) {
	const KIND = GUARD_KINDS[guardKind];
	const isMobile = useVVStore(state => state.isMobile());
	return <Box className='guard-kind-box' tabIndex={0}
		position='relative'
		_hover={isMobile ? {} : { transform: selected ? 'scale(1.2)' : 'scale(1.1)' }}
		{...selected && { transform: 'scale(1.15)', zIndex: 10 }}
		transition='transform .1s ease-out'
		cursor='pointer'
		{...rest}>
		<Image src={KIND.src} height='100px' />
		<Box position='absolute' bottom='0px' right={0} left={0} display='flex' justifyContent='center'>
			<Box
				bg={selected ? 'primary.400' : '#000'}
				color={selected ? '#000' : '#fffc'}
				boxShadow={selected ? '0px 1px 1px -1px #000000' : 'none'}

				transition='color .1s ease-out'
				lineHeight='normal' p='2px .6em' fontSize='xs' fontWeight='500' whiteSpace='nowrap' >
				{guardName || KIND.name}
			</Box>
		</Box>
	</Box>
}

export function GuardKindContainer({ ...rest }: BoxProps) {
	return <Box className='guard-kind-container' boxShadow='0px 0px 14px -5px inset #000' borderRadius='md'
		bg='linear-gradient(360deg, #4ebb99, #1f6f64 31%, #fffd 31%, #71c2e3)'
		{...rest}
	/>
}



// ---------------------------------------------- LAYOUT ------------------------------------
const EMPTY_GUARD: IGuard = { kind: '00', message: '', name: '', locationTitle: '', longLat: HUNGARY_CENTER };
export interface INewGuardContext {
	newGuard: IGuard;
	setNewGuard: (v: IGuard) => void;
}
export const NewGuardContext = createContext<INewGuardContext>({
	newGuard: { ...EMPTY_GUARD },
	setNewGuard: () => { throw 'no newguard ctx'; }
})


export function ProfileLayout() {
	const myProfile = useVVStore(state => state.myProfile);
	const defaultName = ((myProfile?.userName || '').split(' ').pop() || '');
	const [newGuard, setNewGuard] = useState<IGuard>(myProfile?.guard || { ...EMPTY_GUARD, name: defaultName });
	const newGuardCtx = useMemo(() => ({ newGuard, setNewGuard }), [newGuard, setNewGuard]);
	useEffect(() => {
		if (myProfile && myProfile.guard)
			setNewGuard(myProfile.guard);
		else if (myProfile && myProfile.userName)
			setNewGuard({ ...newGuard, name: defaultName });
	}, [myProfile]);

	return <NewGuardContext.Provider value={newGuardCtx}>
		<Outlet />
	</NewGuardContext.Provider>
}

export function ProfileDashboard() {
	const myProfile = useVVStore(state => state.myProfile);
	const auth = useDeepAuth();
	const isMobile = useVVStore(state => state.isMobile());
	const defaultName = ((myProfile?.userName || '').split(' ').pop() || '');


	return <VizvMainCol>
		<VizvCard isClosable>
			<VizvCardHeader>Profilom</VizvCardHeader>
			<AuthWarningBox />
			<VizvCardContent display='flex' flexDir={isMobile ? 'column-reverse' : 'row'} gap={6} >
				<Box display='flex' flexDir='column' alignItems='center'>
					<GuardKindContainer display='inline-flex' flexDir='row' width='200px' justifyContent='space-evenly' py={8} mb={1} >
						<GuardKindBox guardKind={myProfile?.guard?.kind || '00'} guardName={myProfile?.guard?.name || defaultName} />
					</GuardKindContainer>
					{auth.userConfirmed
						? <VizvButtonCancel to='/profile/guard-set/kind' size='xs' variant='ghost' colorScheme='primary' >Váltok</VizvButtonCancel>
						: <VizvButtonCancel onClick={() => auth.showSignIn()} size='xs' variant='ghost' colorScheme='primary' >Váltok</VizvButtonCancel>
					}
				</Box>
				<Box display='flex' flexDir='column'>
					<Box display='flex' flexDir='row' gap={3} alignItems='center'>
						<Image src={auth.userConfirmed?.pictureUrl} />
						<Box>{auth.userConfirmed?.userName}</Box>
						{/*<Box>{auth.user?.guardKind}</Box>*/}
					</Box>
					{myProfile?.guard
						? <UnorderedList className="ulbox" display='flex' flexDir='column' mt={6}>
							<ListItem>Rendfokozatod: <b>{GUARD_KINDS[myProfile.guard.kind].name}</b></ListItem>
							<ListItem>VízVédő neved: <u>{myProfile.guard.name}</u><IconButton as={NavLink} to='/profile/guard-set/name' aria-label="név módosítása" ml={1} variant='ghost' icon={<MdEdit/>} size='sm' /></ListItem>
							<ListItem>Címed: <u>{myProfile.guard.locationTitle}</u><IconButton as={NavLink} to='/profile/guard-set/location' aria-label="cím módosítása" ml={1} variant='ghost' icon={<MdEdit/>} size='sm' /></ListItem>
							<ListItem>Vállalásod: <Text fontStyle='italic' >{myProfile.guard.message}</Text></ListItem>
						</UnorderedList>



						: <Box>
							Még nincs VízVédő profilod

						</Box>
					}

				</Box>
			</VizvCardContent>
			<VizvCardActionStrip>
				<VizvCardAction to='/profile/guard-set/kind' >{myProfile?.guard ? "Módosítom" : "VízVédő leszek"}</VizvCardAction>
			</VizvCardActionStrip>

		</VizvCard >
	</VizvMainCol>
}


// =========================================================================== GUARD-KIND-APPROVE ========================================
export function ProfileGuardKindApprove() {
	const newGuardCtx = useContext(NewGuardContext);
	const selectedKind = newGuardCtx.newGuard.kind;
	const setGuardName = (v: string) => {
		newGuardCtx.setNewGuard({ ...newGuardCtx.newGuard, name: v });
	}
	const setGuardMessage = (v: string) => {
		newGuardCtx.setNewGuard({ ...newGuardCtx.newGuard, message: v });
	}
	const [nameError, setNameError] = useState('');
	useEffect(() => {
		let name = newGuardCtx.newGuard.name || '';
		if (name.length < GUARD_NAME_MIN)
			setNameError(`Minimum ${GUARD_NAME_MIN} karakter`);
		else if (name.length > GUARD_NAME_MAX)
			setNameError(`Maximum ${GUARD_NAME_MAX} karakter`);
		else
			setNameError('');

	}, [newGuardCtx.newGuard.name])
	const [messageError, setMessageError] = useState('');
	useEffect(() => {
		let message = newGuardCtx.newGuard.message || '';
		/*if (message.length < AFFAIR_DESCRIPTION_MIN)
			setMessageError(`Minimum ${AFFAIR_DESCRIPTION_MIN} karakter. Még kell ${AFFAIR_DESCRIPTION_MIN - message.length}`);
		else*/ if (message.length > AFFAIR_DESCRIPTION_MAX)
			setMessageError(`Maximum ${AFFAIR_DESCRIPTION_MAX} karakter`);
		else
			setMessageError('');

	}, [newGuardCtx.newGuard.message])
	return <VizvMainCol>
		<VizvCard>
			<VizvCardHeader>Profilom</VizvCardHeader>
			<VizvCardContent>
				<Box display='flex' flexDir='column' alignItems='center' gap={2} py={2}>
					<GuardKindContainer display='inline-flex' flexDir='row' width='200px' justifyContent='space-evenly' py={8} mb={1} >
						<NavLink to='/profile/guard-set/kind'>
							<GuardKindBox guardKind={newGuardCtx.newGuard.kind || '00'} guardName={newGuardCtx.newGuard.name} />
						</NavLink>
					</GuardKindContainer>
					<FormControl isInvalid={!!nameError}>
						<FormLabel>VízVédő neved:</FormLabel>
						<Input value={newGuardCtx.newGuard.name} onChange={e => setGuardName(e.target.value)} />
						<FormErrorMessage>{nameError}</FormErrorMessage>
						<FormHelperText>Rövid nevet adj! Ez látszik majd a térképen</FormHelperText>
					</FormControl>
					<FormControl isInvalid={!!messageError}>
						<FormLabel>Jelmondatod / vállalásod:</FormLabel>
						<Textarea cols={30} rows={4} value={newGuardCtx.newGuard.message} onChange={e => setGuardMessage(e.target.value)} />
						<FormErrorMessage>{messageError}</FormErrorMessage>
						<FormHelperText>Ezt láthatják majd a többiek, ha a térképen a VízVédő figurádra kattintanak.</FormHelperText>
					</FormControl>
				</Box>
			</VizvCardContent>
			<VizvCardActionStrip>
				<VizvButtonCancel to='/profile' >Mégsem</VizvButtonCancel>
				<VizvCardAction to='/profile/guard-set/location'>{GUARD_KINDS[selectedKind].name} leszek</VizvCardAction>
			</VizvCardActionStrip>
		</VizvCard>
	</VizvMainCol>
}


// =========================================================================== GUARD-KIND-SELECT ========================================
export function ProfileGuardKindSelector() {
	const newGuardCtx = useContext(NewGuardContext);
	const setSelectedKind = (v: TGuardKind) => {
		newGuardCtx.setNewGuard({ ...newGuardCtx.newGuard, kind: v });
	}
	const selectedKind = newGuardCtx.newGuard.kind;
	const isFull = useVVStore(state => state.isFull());
	const UL_CLASS = 'ulboxx';

	return <VizvMainCol>
		<VizvCard>
			<VizvCardHeader>Profilom</VizvCardHeader>
			<VizvCardContent>
				<Box
					{...isFull && {
						width: '1024px'
					}}>
					<Box display='flex' justifyContent='center' alignItems='baseline' position='relative' top='-.7em'
					><Box>Kattints a <u>VízVédő</u> figurára, hogy megtudd mit kell vállalnod!</Box></Box>
					<GuardKindContainer overflowX='auto' mb={3}>
						<Box display='flex' flexDir='row' justifyContent='space-evenly' alignItems='flex-end' p={8} gap={6} minWidth='500px' >
							{GUARD_KINDS_ARR.map(guardKind =>
								<GuardKindBox key={guardKind.key} guardKind={guardKind.key} selected={guardKind.key === selectedKind} onClick={() => setSelectedKind(guardKind.key)} />
							)}
						</Box>
					</GuardKindContainer>
					<Box minHeight='10em' mb='-0em'>
						{selectedKind === '00' && <Box>
							<UnorderedList className={UL_CLASS}>
								<ListItem>Bejelentem és feltöltöm az adatbázisba a <b>saját észlelésemet.</b></ListItem>
							</UnorderedList>
						</Box>}
						{selectedKind === '10' && <Box>
							<UnorderedList className={UL_CLASS}>
								<ListItem>Bejelentem és feltöltöm az adatbázisba a <b>saját észlelésemet.</b></ListItem>
								<ListItem>Megosztom a <PeticioLink>Víz Koalíció petícióját</PeticioLink>, híreit, bejegyzéseit.</ListItem>
							</UnorderedList>
						</Box>}
						{selectedKind === '20' && <Box>
							<UnorderedList className={UL_CLASS}>
								<ListItem>Regisztrált VízVédő leszek!</ListItem>
								<ListItem>Vállalom, hogy mások jelzéseit figyelem, a témába vágó bejegyzések alá bekommentelem a VízVédő honlapot és kérem a posztolót, hogy töltse fel ide az észlelését (képét, történetét, videóját)
									a <u>településemen</u>
								</ListItem>
								<ListItem>Megosztom a <PeticioLink>Víz Koalíció petícióját</PeticioLink>, híreit, bejegyzéseit.</ListItem>
							</UnorderedList>
						</Box>}
						{selectedKind === '25' && <Box>
							<UnorderedList className={UL_CLASS}>
								<ListItem>Regisztrált VízVédő leszek!</ListItem>
								<ListItem>Vállalom, hogy mások jelzéseit figyelem, a témába vágó bejegyzések alá bekommentelem a VízVédő honlapot és kérem a posztolót, hogy töltse fel ide az észlelését (képét, történetét, videóját)
									az <b>ország egész területén</b>
								</ListItem>
								<ListItem>Figyelem a közösségi média vizes tematikájú bejegyzéseit, és szükség esetén feltöltöm ezeket az adatbázisba</ListItem>
								<ListItem>Megosztom a <PeticioLink>Víz Koalíció petícióját</PeticioLink>, híreit, bejegyzéseit.</ListItem>
							</UnorderedList>
						</Box>}
						{selectedKind === '30' && <Box>
							<UnorderedList className={UL_CLASS}>
								<ListItem>VízVédő közösséget építek online (Facebook-csoport) vagy offline</ListItem>
								<ListItem>Ehhez igénybe veszem a Víz Koalíció segítségét és képzéseit.</ListItem>
								<ListItem>A bejelentésekben aktív posztolókat motiválom, hogy lépjenek be a VízVédők helyi csapatába.</ListItem>
								<ListItem>Helyi vizes eseményeket szervezek, és csapatommal részt veszek a <VizKoalicioLink /> országos eseményein.</ListItem>
							</UnorderedList>
						</Box>}
					</Box>

				</Box>
			</VizvCardContent>
			<VizvCardActionStrip>
				<VizvButtonCancel to='/profile'  >Mégsem</VizvButtonCancel>
				<VizvCardAction to='/profile/guard-set/name'>{GUARD_KINDS[selectedKind].name} leszek</VizvCardAction>
			</VizvCardActionStrip>
		</VizvCard>
	</VizvMainCol>
}




// =========================================================================== SELECT LOCATION ========================================
export function ProfileMyLocation() {
	const newGuardCtx = useContext(NewGuardContext);

	const store = useVVStore();
	const api = useVVApi();
	const authCtx = useContext(DeepAuthContext);
	const nav = useNavigate();

	const onCancel = () => {
		nav('/profile');
	}

	useEffect(() => {
		if (authCtx.userConfirmed) {
			store.removeGuardItem(authCtx.userConfirmed)
			if (authCtx.userConfirmed.guard)
				store.setCreateLngLat(authCtx.userConfirmed.guard.longLat);
		}

		return () => {
			if (authCtx.userConfirmed)
				store.addGuardItems([authCtx.userConfirmed])
		}

	}, []);

	const addressConfirmReady = (place: Place, locationTitle: string) => {
		if (!place || !place.Geometry || !place.Geometry.Point) // TODO: nem itt kell ezt.
			return;

		const saveGuard = { ...newGuardCtx.newGuard, longLat: place.Geometry.Point as TCoordinate, locationTitle }
		newGuardCtx.setNewGuard(saveGuard)
		nav('/profile/guard-set/confirm');
	}

	return <VizvMainCol isTool={{ width: 400, isDetailed: false }}>
		<AffairLocation onCancel={onCancel} newItem={{
			kind: 'Guard',
			subKind: newGuardCtx.newGuard.kind,
			isNew: true,
			title: newGuardCtx.newGuard.name,
		}}

			headStrip={<>
				<VizvCardHeaderPopup variant="blue">Elhelyezem magam!</VizvCardHeaderPopup>
			</>}
			onSelect2={addressConfirmReady}


		/>

	</VizvMainCol>
}



// =========================================================================== CONFIRM ========================================
export function ProfileConfirm() {
	const authCtx = useDeepAuth();
	const newGuardCtx = useContext(NewGuardContext);
	const [isLoading, setIsLoading] = useBoolean();
	const [error, setError] = useState('');
	const api = useVVApi();
	const store = useVVStore();
	const nav = useNavigate();


	// ============================== FELÍRÁS ==================================
	const doSave = async () => {
		if (!authCtx.userConfirmed) {
			alert('no user');
			return;
		}
		setIsLoading.on();

		setError('');
		try {
			///debugger;
			const resp = await api.fetch<IProfileGuardPostApi>({
				url: '/auth/profile/guard',
				method: 'POST',
				payload: {
					guard: newGuardCtx.newGuard
				}
			}
			);
			if (resp.user.guard) {
				store.setMyProfile(resp.user);
			}

			store.setMyProfile(resp.user);
			store.addGuardItems([resp.user]);
			store.addGroupItems(resp.groups);
			store.setSuggestionList([]);
			store.setMapMode('SHOW');
			store.setCreateLngLat(undefined);
			nav('/map');

		} catch (ex) {
			setError('' + ex);
		} finally {
			setIsLoading.off();
		}

	}


	return <VizvMainCol>
		<VizvCard isClosable>
			<DeepErrorContainer isLoading={isLoading} error={error} onErrorClose={() => setError('')} >
				<VizvCardHeader>Új profilom</VizvCardHeader>
				<VizvCardContent>
					<FormControl>
						<FormLabel>Vízvédő rangom</FormLabel>
						<Input defaultValue={GUARD_KINDS[newGuardCtx.newGuard.kind]?.name} isReadOnly />
					</FormControl>
					<FormControl>
						<FormLabel>Vízvédő nevem</FormLabel>
						<Input defaultValue={newGuardCtx.newGuard.name}  isReadOnly />
					</FormControl>
					<FormControl>
						<FormLabel>Helyem</FormLabel>
						<Input defaultValue={newGuardCtx.newGuard.locationTitle} isReadOnly />
						<FormHelperText>{newGuardCtx.newGuard.longLat[0]} / {newGuardCtx.newGuard.longLat[1]}</FormHelperText>
					</FormControl>
					<FormControl>
						<FormLabel>Jelmondatom</FormLabel>
						<Textarea defaultValue={newGuardCtx.newGuard.message}  isReadOnly />
					</FormControl>					


				</VizvCardContent>
				<VizvCardActionStrip>
					<VizvButtonCancel to='/profile' >Mégsem</VizvButtonCancel>
					<VizvCardAction onClick={doSave}>Profil mentése</VizvCardAction>
				</VizvCardActionStrip>
			</DeepErrorContainer>
		</VizvCard>
	</VizvMainCol>

}